import { useId, type ReactNode, type InputHTMLAttributes, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';

interface FieldShellProps {
  label: string;
  hint?: ReactNode;
  error?: string | undefined;
  required?: boolean;
  wide?: boolean;
  children: (ids: { inputId: string; describedBy: string | undefined; invalid: boolean }) => ReactNode;
}

/**
 * Wires up label, hint and error text with the right ARIA attributes so screen
 * readers announce validation messages alongside the input.
 */
function FieldShell({ label, hint, error, required, wide, children }: FieldShellProps) {
  const inputId = useId();
  const hintId = `${inputId}-hint`;
  const errorId = `${inputId}-error`;

  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ');

  return (
    <div className={wide ? 'field field-wide' : 'field'}>
      <label htmlFor={inputId}>
        {label}
        {required && (
          <span className="required-marker" aria-hidden="true">
            *
          </span>
        )}
        {required && <span className="visually-hidden"> (required)</span>}
      </label>
      {hint && (
        <span className="field-hint" id={hintId}>
          {hint}
        </span>
      )}
      {children({ inputId, describedBy: describedBy || undefined, invalid: Boolean(error) })}
      {error && (
        <span className="field-error" id={errorId} role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

type TextFieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'aria-invalid'> & {
  label: string;
  hint?: ReactNode;
  error?: string | undefined;
  wide?: boolean;
};

export function TextField({ label, hint, error, wide, required, ...inputProps }: TextFieldProps) {
  return (
    <FieldShell label={label} hint={hint} error={error} required={required} wide={wide}>
      {({ inputId, describedBy, invalid }) => (
        <input
          {...inputProps}
          id={inputId}
          required={required}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
        />
      )}
    </FieldShell>
  );
}

type SelectFieldProps = Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id' | 'aria-invalid'> & {
  label: string;
  hint?: ReactNode;
  error?: string | undefined;
  wide?: boolean;
  options: { value: string; label: string }[];
  placeholder?: string;
};

export function SelectField({
  label,
  hint,
  error,
  wide,
  required,
  options,
  placeholder,
  ...selectProps
}: SelectFieldProps) {
  return (
    <FieldShell label={label} hint={hint} error={error} required={required} wide={wide}>
      {({ inputId, describedBy, invalid }) => (
        <select
          {...selectProps}
          id={inputId}
          required={required}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
        >
          {placeholder !== undefined && <option value="">{placeholder}</option>}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      )}
    </FieldShell>
  );
}

type TextAreaFieldProps = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id' | 'aria-invalid'> & {
  label: string;
  hint?: ReactNode;
  error?: string | undefined;
  wide?: boolean;
};

export function TextAreaField({
  label,
  hint,
  error,
  wide = true,
  required,
  ...textareaProps
}: TextAreaFieldProps) {
  return (
    <FieldShell label={label} hint={hint} error={error} required={required} wide={wide}>
      {({ inputId, describedBy, invalid }) => (
        <textarea
          {...textareaProps}
          id={inputId}
          required={required}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
        />
      )}
    </FieldShell>
  );
}

interface CheckboxFieldProps {
  label: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  error?: string | undefined;
  name?: string;
  required?: boolean;
  inline?: boolean;
  disabled?: boolean;
}

export function CheckboxField({
  label,
  checked,
  onChange,
  error,
  name,
  required,
  inline,
  disabled,
}: CheckboxFieldProps) {
  const id = useId();
  const errorId = `${id}-error`;

  return (
    <div className={inline ? undefined : 'field field-wide'}>
      <label className={inline ? 'checkbox-inline' : 'checkbox'} htmlFor={id}>
        <input
          type="checkbox"
          id={id}
          name={name}
          checked={checked}
          disabled={disabled}
          required={required}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span className="checkbox-text">
          {label}
          {required && (
            <span className="required-marker" aria-hidden="true">
              *
            </span>
          )}
        </span>
      </label>
      {error && (
        <span className="field-error" id={errorId} role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
